import React, { useEffect, useState } from "react";
import { Loader2, X, UserCog, AlertTriangle } from "lucide-react";

/**
 * Coordinator modal for moving a draft from one RN to another mid-flight.
 *
 * Shipping it next to the row keeps the workflow zero-context-switch:
 * the coordinator already has the draft selected, this just changes the
 * owner. Audit trail is captured server-side; AuditDrawer surfaces it
 * back into the same page.
 */
export default function ReassignModal({ open, draft, onClose, onConfirm }) {
  const [newEmail, setNewEmail] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setNewEmail("");
      setReason("");
      setBusy(false);
      setErr("");
    }
  }, [open]);

  if (!open || !draft) return null;

  const trimmed = newEmail.trim();
  const looksLikeEmail = trimmed && trimmed.includes("@") && trimmed.split("@")[1]?.includes(".");
  const sameAsCurrent = trimmed.toLowerCase() === (draft.assigned_rn || "").trim().toLowerCase();
  const submittable = looksLikeEmail && !sameAsCurrent && !busy;

  const submit = async () => {
    if (!looksLikeEmail) { setErr("Please enter a valid email address."); return; }
    if (sameAsCurrent) { setErr("That's already the current owner."); return; }
    setBusy(true); setErr("");
    try {
      await onConfirm({ newRnEmail: trimmed, reason: reason.trim() });
    } catch (e) {
      setErr(e?.message || "Reassign failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0B1E3A]/40 flex items-center justify-center px-4 py-12"
      data-testid="reassign-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#3A5275]">
              Reassign draft
            </div>
            <h2 className="font-display text-xl font-bold text-[#0B1E3A] mt-1">
              {draft.client_name || "(unnamed client)"}
            </h2>
            <div className="text-[12px] text-[#3A5275] mt-1">
              Currently assigned to{" "}
              <span className="font-mono text-[#0A3D79]" data-testid="reassign-current-rn">
                {draft.assigned_rn || "(unassigned)"}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-[#3A5275] hover:text-[#0B1E3A] disabled:opacity-50"
            data-testid="reassign-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3 mb-4">
          <label className="block">
            <span className="text-[12px] font-semibold text-[#0B1E3A]">
              New RN email <span className="text-[#A42A2A]">*</span>
            </span>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="rn-bob@avidacare.com"
              disabled={busy}
              autoFocus
              data-testid="reassign-new-email"
              className="mt-1 w-full rounded-lg border border-[#D6E3EF] px-3 py-2 text-[14px] focus:border-[#0A3D79] focus:outline-none focus:ring-1 focus:ring-[#0A3D79]"
            />
            <span className="text-[11px] text-[#3A5275] mt-1 block">
              The new owner's JWT email must match exactly (case-insensitive).
            </span>
          </label>

          <label className="block">
            <span className="text-[12px] font-semibold text-[#0B1E3A]">
              Reason <span className="text-[#3A5275] font-normal">(optional, recommended)</span>
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Original RN out sick, schedule change…"
              disabled={busy}
              rows={3}
              maxLength={500}
              data-testid="reassign-reason"
              className="mt-1 w-full rounded-lg border border-[#D6E3EF] px-3 py-2 text-[14px] focus:border-[#0A3D79] focus:outline-none focus:ring-1 focus:ring-[#0A3D79] resize-none"
            />
            <span className="text-[11px] text-[#3A5275] mt-1 block">
              Stored in the audit trail so future coordinators understand the move.
            </span>
          </label>
        </div>

        <div className="mb-4 rounded-lg border border-[#F0E29A] bg-[#FFFDEC] px-3 py-2 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-[#8A6B00] shrink-0 mt-0.5" />
          <div className="text-[12px] text-[#8A6B00]">
            The previous RN's session will see the rn_mismatch block screen on next open.
            Wizard progress (autosave) is preserved.
          </div>
        </div>

        {err && (
          <div
            className="mb-4 rounded-lg border border-[#E6B8B8] bg-[#FBECEC] px-3 py-2 text-[12.5px] text-[#A42A2A]"
            data-testid="reassign-error"
          >
            {err}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="btn-ghost"
            data-testid="reassign-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!submittable}
            className="btn-primary"
            data-testid="reassign-confirm"
          >
            {busy
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Reassigning…</>
              : <><UserCog className="w-4 h-4" /> Reassign</>}
          </button>
        </div>
      </div>
    </div>
  );
}
